import { captureException } from '@sentry/browser'
import { LobsterMasterApi } from '@ulysses-inc/harami_api_client'
import { useState } from 'react'
import { useSnackbar } from 'src/components/snackbar/useSnackbar'
import { baseClient } from 'src/state/middleware/saga/baseClient'

const MAX_UPLOAD_IMAGE_LONG_SIDE = 800

export const useUploadLabelImage = () => {
  const [isUploading, setIsUploading] = useState(false)
  const { showErrorSnackbar, showSuccessSnackbar } = useSnackbar()
  const api = baseClient.getApi(LobsterMasterApi)

  const uploadLabelImage = async (file: File, path: string) => {
    setIsUploading(true)
    try {
      const presignedUrlResponse = await api.postInternalImageUploadUrl({
        postInternalImageUploadUrlRequestBody: { path },
      })
      const presignedUrl = presignedUrlResponse.url

      const jpegFile = await convertToJpeg(file)
      if (!jpegFile.isSuccess) {
        captureException(new Error(jpegFile.error))
        showErrorSnackbar(jpegFile.error)
        return undefined
      }

      const response = await fetch(presignedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': 'image/jpeg' },
        body: jpegFile.file,
      })

      if (!response.ok) {
        captureException(new Error(response.statusText))
        showErrorSnackbar('画像のアップロードに失敗しました')
        return undefined
      }

      const createImageResponse = await api.postInternalImages({
        postInternalImageRequestBody: { path },
      })
      const id = createImageResponse.id

      const imageUrlResponse = await api.postInternalImageUrl({
        postInternalImageUrlRequestBody: { id },
      })
      const imageUrl = imageUrlResponse.url

      showSuccessSnackbar('画像のアップロードに成功しました')
      return { imageUrl, id }
    } catch (error) {
      captureException(error)
      showErrorSnackbar('画像のアップロードに失敗しました')
      return undefined
    } finally {
      setIsUploading(false)
    }
  }

  return { uploadLabelImage, isUploading }
}

type ConvertResult =
  | { isSuccess: true; file: File }
  | { isSuccess: false; error: string }

const resizeImage = (blob: Blob, fileName: string): Promise<ConvertResult> => {
  return new Promise(resolve => {
    const img = new Image()
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const objectUrl = URL.createObjectURL(blob)

    img.onload = () => {
      URL.revokeObjectURL(objectUrl)

      let width = img.width
      let height = img.height

      if (width > height && width > MAX_UPLOAD_IMAGE_LONG_SIDE) {
        height = Math.round((height * MAX_UPLOAD_IMAGE_LONG_SIDE) / width)
        width = MAX_UPLOAD_IMAGE_LONG_SIDE
      } else if (height > MAX_UPLOAD_IMAGE_LONG_SIDE) {
        width = Math.round((width * MAX_UPLOAD_IMAGE_LONG_SIDE) / height)
        height = MAX_UPLOAD_IMAGE_LONG_SIDE
      }
      canvas.width = width
      canvas.height = height
      ctx?.drawImage(img, 0, 0, width, height)

      canvas.toBlob(
        resultBlob => {
          if (resultBlob) {
            const baseName = fileName.replace(/\.[^.]+$/, '')
            const jpegFile = new File([resultBlob], `${baseName}.jpg`, {
              type: 'image/jpeg',
              lastModified: Date.now(),
            })
            resolve({ isSuccess: true, file: jpegFile })
          } else {
            resolve({ isSuccess: false, error: 'JPEG変換に失敗しました' })
          }
        },
        'image/jpeg',
        1,
      )
    }

    img.onerror = () => {
      URL.revokeObjectURL(objectUrl)
      resolve({ isSuccess: false, error: '画像の読み込みに失敗しました' })
    }

    img.src = objectUrl
  })
}

const convertHeicToJpeg = async (
  file: File,
): Promise<
  { isSuccess: true; file: Blob } | { isSuccess: false; error: string }
> => {
  try {
    const { heicTo } = await import('heic-to')
    const jpegBlob = await heicTo({
      blob: file,
      type: 'image/jpeg',
      quality: 1,
    })
    return { isSuccess: true, file: jpegBlob }
  } catch {
    return { isSuccess: false, error: 'HEIC画像の変換に失敗しました' }
  }
}

const isHeicFile = (file: File): boolean => {
  const lowerName = file.name.toLowerCase()
  return (
    file.type === 'image/heic' ||
    file.type === 'image/heif' ||
    lowerName.endsWith('.heic') ||
    lowerName.endsWith('.heif')
  )
}

const convertToJpeg = async (file: File): Promise<ConvertResult> => {
  let fileToBlob: Blob = file

  if (isHeicFile(file)) {
    const converted = await convertHeicToJpeg(file)
    if (!converted.isSuccess) {
      return { isSuccess: false, error: converted.error }
    }
    fileToBlob = converted.file
  }

  const resized = await resizeImage(fileToBlob, file.name)
  if (!resized.isSuccess) {
    return {
      isSuccess: false,
      error: `画像のリサイズに失敗しました。原因：${resized.error}`,
    }
  }

  return { isSuccess: true, file: resized.file }
}
